import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PageDetail = () => {
    const { pageId } = useParams();
    const { api } = useAuth();
    const [page, setPage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newFormTitle, setNewFormTitle] = useState('');
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchUserRole = async () => {
        try {
            const response = await api.get('/api/user-role/');
            setUserRole(response.data.role);
        } catch (error) {
            console.error("Failed to fetch user role:", error);
        }
    };

    const fetchPageDetails = async () => {
        try {
            const response = await api.get(`/api/pages/${pageId}/`);
            setPage(response.data);
        } catch (error) {
            console.error("Failed to fetch page details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserRole();
        fetchPageDetails();
    }, [pageId, api]);

    const handleCreateForm = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/api/pages/${pageId}/forms/`, { title: newFormTitle });
            fetchPageDetails(); // Refetch to show the new form
            setNewFormTitle('');
            setIsModalOpen(false);
        } catch (error) {
            console.error("Failed to create form:", error);
        }
    };

    if (loading) return <p className="text-center p-8">Loading page details...</p>;
    if (!page) return <p className="text-center p-8">Page not found.</p>;

    const isAdmin = userRole === 'Admin' || userRole === 'superuser';
    const isMember = userRole === 'Resident';

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <Link to="/" className="text-sm text-indigo-600 hover:underline mb-2 block">&larr; Back to Dashboard</Link>
                    <h1 className="text-3xl font-bold text-gray-900">{page.title}</h1>
                    {userRole && (
                        <p className="text-sm text-gray-600 mt-1">
                            Access Level: {isAdmin ? 'Admin' : isMember ? 'Member' : userRole}
                        </p>
                    )}
                </div>
            </header>
            <main>
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    {/* Role-based info message */}
                    {isAdmin && (
                        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
                            <h3 className="text-lg font-medium text-blue-900">Admin Access</h3>
                            <p className="text-blue-700 mt-1">
                                You can create, edit, and manage forms on this page.
                            </p>
                        </div>
                    )}
                    {isMember && (
                        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                            <h3 className="text-lg font-medium text-green-900">Member Access</h3>
                            <p className="text-green-700 mt-1">
                                You can view and interact with forms. Contact an admin to create new forms.
                            </p>
                        </div>
                    )}
                    
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold">Forms on this Page</h2>
                        {isAdmin && (
                            <button 
                                onClick={() => setIsModalOpen(true)} 
                                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                            >
                                Create New Form
                            </button>
                        )}
                    </div>

                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        {page.forms && page.forms.length > 0 ? (
                            <ul className="divide-y divide-gray-200">
                                {page.forms.map(form => (
                                    <li key={form.id}>
                                        <div className="block hover:bg-gray-50 px-4 py-4 sm:px-6">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-indigo-600 truncate">{form.title}</p>
                                                    {form.description && (
                                                        <p className="text-sm text-gray-500 mt-1">{form.description}</p>
                                                    )}
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {form.is_active ? 'Active' : 'Inactive'} • 
                                                        {form.submissions?.length || 0} submissions
                                                    </p>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    {isAdmin ? (
                                                        <>
                                                            <Link
                                                                to={`/page/${pageId}/form-builder/${form.id}`}
                                                                className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                                                            >
                                                                Edit Form
                                                            </Link>
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                Admin
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Link 
                                                                to={`/form/${form.public_link_id}`}
                                                                className="text-green-600 hover:text-green-900 text-sm font-medium"
                                                            >
                                                                View Form
                                                            </Link>
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                View Only
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="p-8 text-center">
                                <p className="text-gray-500 text-lg">No forms have been created for this page yet.</p>
                                {isAdmin && (
                                    <p className="text-gray-400 mt-2">Click "Create New Form" to get started.</p>
                                )}
                                {isMember && (
                                    <p className="text-gray-400 mt-2">Contact an admin to create forms.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Create Form Modal - Only for Admins */}
            {isModalOpen && isAdmin && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
                    <div className="bg-white p-5 rounded-lg shadow-xl w-full max-w-md">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Create a New Form</h3>
                        <form onSubmit={handleCreateForm} className="mt-2">
                            <input 
                                type="text" 
                                value={newFormTitle} 
                                onChange={(e) => setNewFormTitle(e.target.value)}
                                placeholder="Enter form title (e.g., Complaint Form)" 
                                required
                                className="w-full p-2 border border-gray-300 rounded-md" 
                            />
                            <div className="mt-4 flex justify-end space-x-2">
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)} 
                                    className="px-4 py-2 bg-gray-200 rounded-md"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md"
                                >
                                    Create Form
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PageDetail;